import React from "react";
import { Link } from "react-router-dom";
import { Header, ExpandCollapseAll, Pager } from "../components";
import EditorPagerLink from "../components/EditorPagerLink";
import { anchorSvg } from "../components/HeaderWithAnchor";
import ExternalLinkIcon from "../../ExternalLinkIcon.component";
import useEditorAccess from "../../../hooks/useEditorAccess.hook";
import {
  ACR_EDITOR_ABOUT_URL,
  ACR_EDITOR_REPORT_URL,
} from "../../../utlities/core.constants";
import "../components/Criteria.css";
import "./Overview.page.css";

export default function Overview() {
  const hasEditorAccess = useEditorAccess();

  return (
    <div className="overview-page space-y-4">
        <Header>Overview</Header>

        <p>
          The ACR Editor is a web-based tool designed to help evaluators build Accessibility
          Conformance Reports (ACRs). This editor assists accessibility subject matter experts in
          creating machine-readable ACR documents for digital products or services. Users are
          guided through the process of documenting conformance for each requirement, enabling
          them to generate accessibility reports, including Section 508 reports.
        </p>

        <p className="overview-tips-heading">Tips for using this tool:</p>
        <ul className="overview-list">
          <li>
            The ACR Editor saves the information you enter locally in your browser while you
            work. When you are finished, save your ACR to your account for buyers to browse or
            save it privately to reveal at a later date. You also have the option to download
            your ACR. If you need to edit an ACR, you can do so in the future by re-opening
            the ACR in the editor.
          </li>
          <li>
            As you use the editor, the{" "}
            <Link to={ACR_EDITOR_REPORT_URL}>&ldquo;Report&rdquo;</Link>{" "}
            page lists success criteria that you have either checked or left unchecked.
          </li>
          <li>
            You can export your report in YAML (structured data format) or HTML (web page
            format).
          </li>
          <li>
            Add limited formatting to your report with{" "}
            <a href="https://www.markdownguide.org/basic-syntax/" target="_blank" rel="noreferrer">
              Markdown
              <ExternalLinkIcon />
              <span className="usa-sr-only"> (opens in new window)</span>
            </a>
            . Markdown allows you to include lists, links, and code examples.
          </li>
        </ul>

        <ExpandCollapseAll />

        {/* About ACRs */}
        <details className="criteria-details">
          <summary>
            <Link
              to="#about-acr-overview"
              className="criteria-anchor-link"
              aria-hidden="true"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
            >
              {anchorSvg}
            </Link>
            <span className="criteria-summary-inner">
              <h2 id="about-acr-overview" className="criteria-title">
                About ACRs
              </h2>
            </span>
          </summary>
          <p className="mb-3">
            The OpenACR format is a machine-readable Accessibility Conformance Report (ACR) initially
            developed to align with Section 508 requirements. It offers numerous advantages,
            including the ability to compare accessibility claims of digital products and services.
            The highly structured, self-validated format simplifies comparison and contributes to
            accessible service delivery.
          </p>
          <p className="mb-3">
            Traditionally, ACRs are submitted in PDF format, which is often inaccessible,
            inconsistently structured, and difficult to search. The OpenACR format modernizes this
            process by enabling validation to confirm that conformance claims match required
            standards.
          </p>
          <p className="mb-2">When documenting accessibility findings:</p>
          <ul className="overview-list">
            <li>
              Responsible organizations review vendor submissions, including conformance claims.
            </li>
            <li>Independent testing supplements these reviews.</li>
            <li>Using a standardized format simplifies comparisons and report updates.</li>
          </ul>
        </details>

        {/* Structure of this tool */}
        <details className="criteria-details">
          <summary>
            <Link
              to="#structure-overview"
              className="criteria-anchor-link"
              aria-hidden="true"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
            >
              {anchorSvg}
            </Link>
            <span className="criteria-summary-inner">
              <h2 id="structure-overview" className="criteria-title">
                Structure of this tool
              </h2>
            </span>
          </summary>
          <p className="mb-3">
            The ACR Editor walks users through a series of <strong>chapters</strong> based on
            the catalog you select. Supported combinations include WCAG only, WCAG + Section
            508, WCAG + EU, and WCAG + Section 508 + EU, so the number of chapters will vary.
            Each chapter contains a number of <strong>criteria</strong>, which are further
            divided into <strong>components</strong>. For each, you can select a level and
            type in a note about conformance.
          </p>
          <p className="mb-3">
            A, AA, AAA are for the <strong>WCAG 2</strong> guidelines.
          </p>
          <p className="mb-3">
            FPC, Hardware, Software, Docs is the <strong>Section 508</strong> standards.
          </p>
          <p className="mb-3">
            Functional, Generic, ICT - Voice, ICT - Video, Hardware (EU), Web, Non-Web,
            Software (EU), Docs (EU), and ICT - Emergency are for the{" "}
            <strong>EN 301 549</strong> standards.
          </p>
          <p>
            Links in this editor will open a new window that lead you to the relevant sections
            of the guidelines.
          </p>
        </details>

        {/* Terms */}
        <details className="criteria-details">
          <summary>
            <Link
              to="#terms-overview"
              className="criteria-anchor-link"
              aria-hidden="true"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
            >
              {anchorSvg}
            </Link>
            <span className="criteria-summary-inner">
              <h2 id="terms-overview" className="criteria-title">
                Terms
              </h2>
            </span>
          </summary>
          <p className="mb-3">
            While documenting conformance, users will select conformance terms for each component.
            Definitions of those selections:
          </p>
          <dl className="overview-terms">
            <div>
              <dt>Supports</dt>
              <dd>
                The product functionality meets the criterion without known defects or with
                equivalent facilitation.
              </dd>
            </div>
            <div>
              <dt>Partially Supports</dt>
              <dd>Some product functionality does not meet the criterion.</dd>
            </div>
            <div>
              <dt>Does Not Support</dt>
              <dd>The majority of product functionality does not meet the criterion.</dd>
            </div>
            <div>
              <dt>Not Applicable</dt>
              <dd>The criterion does not apply to the product.</dd>
            </div>
            <div>
              <dt>Not Evaluated</dt>
              <dd>
                The criterion has not been evaluated for the product. This term is only
                applicable for WCAG 2.x Level AAA.
              </dd>
            </div>
          </dl>
        </details>

        {hasEditorAccess && (
          <Pager label="Previous/Next">
            <EditorPagerLink to={ACR_EDITOR_ABOUT_URL} direction="next">
              About
            </EditorPagerLink>
          </Pager>
        )}
      </div>
  );
}
